import { Link } from "react-router-dom";
import { Lock, MapPin, Clock3, Building2, BadgeCheck } from "lucide-react";
import { ago } from "@/lib/api";

export const SHIFT_LABELS = { "1st": "1st shift", "2nd": "2nd shift", "3rd": "3rd shift", weekend: "Weekend" };
export const SHIFT_STYLE = {
  "1st": "bg-sky-50 text-sky-800 border-sky-200", "2nd": "bg-amber-50 text-amber-900 border-amber-200",
  "3rd": "bg-indigo-50 text-indigo-900 border-indigo-200", weekend: "bg-purple-50 text-purple-900 border-purple-200",
};
export const CATEGORY_LABELS = { factory: "Factory & CNC", warehouse: "Warehouse & loading", care: "Caregiving & nursing" };

const fmt = (v) => (Number.isInteger(v) ? v : v.toFixed(2));
export const payText = (j) => {
  if (!j.pay_hourly_min) return "";
  const cur = j.pay_currency === "CAD" ? "CA$" : "$";
  return j.pay_hourly_max && j.pay_hourly_max !== j.pay_hourly_min ? `${cur}${fmt(j.pay_hourly_min)}–${fmt(j.pay_hourly_max)}/hr` : `${cur}${fmt(j.pay_hourly_min)}/hr`;
};

export const ShiftBadge = ({ s }) => (
  <span className={`rounded-md border px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-tight ${SHIFT_STYLE[s] || ""}`} data-testid={`shift-badge-${s}`}>{SHIFT_LABELS[s] || s}</span>
);

export const SourceBadge = ({ agency }) => agency
  ? <span className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700"><Building2 className="h-3 w-3" />Agency role</span>
  : <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800"><BadgeCheck className="h-3 w-3" />Direct employer</span>;

const place = (j) => [j.location && j.location.length < 60 ? j.location : null, !j.location && j.state, j.country === "CA" ? "Canada" : null].filter(Boolean).join(" · ") || j.state || "";

function CardBody({ j, locked }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {locked ? <><Lock className="h-3 w-3" /><span className="select-none blur-[5px]" aria-hidden>Employer hidden</span></> : j.employer}
          <span>· {CATEGORY_LABELS[j.hourly_category]}</span>
        </p>
        <h3 className="mt-1 line-clamp-2 text-base font-semibold sm:text-lg">{j.title}</h3>
        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span><MapPin className="mr-1 inline h-3.5 w-3.5" />{place(j)}</span>
          {j.posted_at && <span><Clock3 className="mr-1 inline h-3.5 w-3.5" />{ago(j.posted_at)}</span>}
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">{(j.shifts || []).map((s) => <ShiftBadge key={s} s={s} />)}<SourceBadge agency={j.is_agency} /></div>
      </div>
      <p className="shrink-0 font-mono text-lg font-extrabold text-emerald-700 sm:text-xl" data-testid="hourly-pay">{payText(j)}</p>
    </div>
  );
}

export function HourlyCard({ j, onLocked }) {
  const cls = "lift block w-full min-w-0 rounded-2xl border bg-white p-4 text-left sm:p-5";
  if (j.locked) {
    return <button type="button" onClick={onLocked} className={cls} data-testid={`hourly-teaser-${j.teaser_id}`}><CardBody j={j} locked /></button>;
  }
  return <Link to={`/app/jobs/${j.job_id}`} className={cls} data-testid={`hourly-card-${j.job_id}`}><CardBody j={j} /></Link>;
}
